import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Award, Calendar, Clock, Activity, MapPin, 
  TrendingUp, Users, CheckCircle, AlertTriangle, 
  Droplet, ChevronDown, ChevronUp, Download
} from 'react-feather';
import { toast } from 'react-hot-toast';

const DonorActivity = ({ dashboardData, loading }) => {
  const [activeTab, setActiveTab] = useState('donations');
  const [expandedDonation, setExpandedDonation] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time since date
  const formatTimeSince = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
  };

  // Handle certificate download
  const handleDownloadCertificate = async (donation) => {
    try {
      // Show loading toast
      toast.loading('Preparing your certificate for download...', {
        id: 'certificate-download',
      });

      // In a real implementation, we would make an API call to get the certificate
      // For example:
      // const response = await api.get(`/donors/certificate/${donation.id}`, { responseType: 'blob' });
      
      // For now, we'll simulate a delay and then show a success message
      setTimeout(() => {
        // Success toast
        toast.success('Certificate downloaded successfully!', {
          id: 'certificate-download',
        });
      }, 1500);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate. Please try again later.', {
        id: 'certificate-download',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  // Add null checks and default values for all dashboard data
  const { 
    donor = {
      name: '',
      bloodType: '',
      lastDonation: null,
      totalDonations: 0,
      donationStreak: 0,
      nextEligibleDate: new Date().toISOString()
    }, 
    donationHistory = [], 
    acceptedRequests = [], 
    upcomingAppointments = [], 
    achievements = [],
    impactMetrics = {
      totalDonations: 0,
      livesSaved: 0,
      hospitalsServed: 0,
      successRate: 0
    }
  } = dashboardData || {};

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Activity Header */}
      <motion.div variants={itemVariants} className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl shadow-md p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-2xl font-bold mb-2">Donor Activity Dashboard</h2>
            <p className="text-red-100">
              Track your donations, appointments, and achievements
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
            <Droplet className="h-5 w-5 text-red-100" />
            <span className="font-medium">{donor.bloodType}</span>
            <span className="text-red-200">|</span>
            <span className="text-red-100">Streak: {donor.donationStreak}</span>
          </div>
        </div>
      </motion.div>

      {/* Activity Tabs */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex border-b">
          <button
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'donations' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('donations')}
          >
            Donation History
          </button>
          <button
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'requests' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('requests')}
          >
            Accepted Requests
          </button>
          <button
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'appointments' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('appointments')}
          >
            Appointments
          </button>
          <button
            className={`flex-1 py-4 text-center font-medium ${
              activeTab === 'achievements' 
                ? 'text-red-600 border-b-2 border-red-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('achievements')}
          >
            Achievements
          </button>
        </div>

        <div className="p-6">
          {/* Donation History Tab */}
          {activeTab === 'donations' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Your Donation History</h3>
                <span className="text-sm text-gray-500">Total: {donationHistory.length}</span>
              </div>
              
              {donationHistory.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <Heart className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">You haven't made any donations yet</p>
                  <p className="text-sm text-gray-400 mt-1">Your donation history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {donationHistory.map((donation, index) => (
                    <div 
                      key={index} 
                      className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-200"
                    >
                      <div 
                        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedDonation(expandedDonation === index ? null : index)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <Heart className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{donation.hospital}</p>
                            <p className="text-sm text-gray-500">{formatDate(donation.date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            donation.status === 'successful' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {donation.status === 'successful' ? 'Successful' : 'Pending Verification'}
                          </span>
                          {expandedDonation === index ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedDonation === index && (
                        <div className="p-4 bg-gray-50 border-t border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Blood Type</p>
                              <p className="font-medium">{donation.bloodType || donor.bloodType}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Units Donated</p>
                              <p className="font-medium">{donation.units || 1}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Donation Type</p>
                              <p className="font-medium">{donation.donationType || 'Whole Blood'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Time Since Donation</p>
                              <p className="font-medium">{formatTimeSince(donation.date)}</p>
                            </div>
                          </div>
                          
                          <div className="flex justify-end mt-4">
                            <button
                              onClick={() => handleDownloadCertificate(donation)}
                              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download Certificate</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Accepted Requests Tab */}
          {activeTab === 'requests' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Accepted Blood Requests</h3>
                <span className="text-sm text-gray-500">Total: {acceptedRequests.length}</span>
              </div>
              
              {acceptedRequests.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <AlertTriangle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">You haven't accepted any requests yet</p>
                  <p className="text-sm text-gray-400 mt-1">Accepted requests will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {acceptedRequests.map((request, index) => (
                    <div 
                      key={index} 
                      className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-200"
                    >
                      <div 
                        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedRequest(expandedRequest === index ? null : index)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Droplet className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {request.patientName || 'Patient'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {typeof request.hospital === 'object' ? request.hospital.name : request.hospital}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            request.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : request.status === 'in_progress'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {request.status === 'completed' 
                              ? 'Completed' 
                              : request.status === 'in_progress'
                                ? 'In Progress'
                                : 'Pending'}
                          </span>
                          {expandedRequest === index ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedRequest === index && (
                        <div className="p-4 bg-gray-50 border-t border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Blood Type</p>
                              <p className="font-medium">{request.bloodType}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Units Needed</p>
                              <p className="font-medium">{request.unitsNeeded || 1}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Requested By</p>
                              <p className="font-medium">
                                {request.requestedBy?.name || 'Hospital Staff'}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Date Accepted</p>
                              <p className="font-medium">
                                {formatDate(request.createdAt)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Status: </span>
                              {request.donors?.find(d => d.donor === donor._id)?.status || 'Accepted'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Appointments</h3>
                <span className="text-sm text-gray-500">Total: {upcomingAppointments.length}</span>
              </div>
              
              {upcomingAppointments.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">You don't have any upcoming appointments</p>
                  <p className="text-sm text-gray-400 mt-1">Schedule an appointment to donate blood</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingAppointments.map((appointment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{appointment.hospital}</p>
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="w-3 h-3 mr-1" />
                              <span>{formatDate(appointment.date)} at {appointment.time}</span>
                            </div>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          appointment.status === 'confirmed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {appointment.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                        </span>
                      </div>
                      
                      <div className="mt-4 flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                          <span>Request ID: </span>
                          <span className="font-mono">{appointment.requestId.substring(0, 8)}...</span>
                        </div>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            Reschedule
                          </button>
                          <button className="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700">
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Your Achievements</h3>
                <span className="text-sm text-gray-500">Total: {achievements.length}</span>
              </div>
              
              {achievements.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <Award className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">You haven't earned any achievements yet</p>
                  <p className="text-sm text-gray-400 mt-1">Start donating to earn achievements</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {achievements.map((achievement, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                          <Award className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{achievement.title}</p>
                          <p className="text-sm text-gray-500">{achievement.description}</p>
                          <p className="text-xs text-gray-400 mt-1">Earned on {formatDate(achievement.date)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {achievements.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Keep up the good work!</p>
                      <p className="text-sm text-gray-600">
                        You're making a difference with every donation. 
                        {donor.donationStreak > 0 && ` Current streak: ${donor.donationStreak}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Impact Summary */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <Heart className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{impactMetrics.livesSaved}</p>
              <p className="text-sm text-gray-500">Lives Impacted</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{impactMetrics.hospitalsServed}</p>
              <p className="text-sm text-gray-500">Hospitals Served</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{impactMetrics.successRate}%</p>
              <p className="text-sm text-gray-500">Success Rate</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DonorActivity;
